import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Receipt, Download } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { customerMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { StatusPill, inr, LoadingScreen, EmptyState } from "@/components/common";

export default function Invoices() {
  const [rows, setRows] = useState(null);
  const [paying, setPaying] = useState("");
  const load = () => api.get("/invoices/mine").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const pay = async (inv) => {
    setPaying(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/pay`);
      toast.success(`Payment successful! Receipt ${data.payment?.receipt_no}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); } finally { setPaying(""); }
  };

  return (
    <DashboardShell menu={customerMenu} title="Invoices & Payments">
      <PageHeader title="Invoices" subtitle="Pay securely online. Receipts are auto-generated." />
      <div className="mb-4 text-xs text-navy-500 bg-navy-50 border border-navy-200 rounded-lg px-4 py-2 inline-block">
        Payments are processed via a gateway-agnostic flow (Razorpay-ready). Current settlement is <b>simulated</b> for demo.
      </div>
      {!rows ? <LoadingScreen /> : rows.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" hint="Invoices appear here once your contract is active." />
      ) : (
        <div className="bg-white border border-navy-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase">
              <tr><th className="text-left p-3">Invoice</th><th className="text-left p-3">Agency</th><th className="p-3">Amount</th><th className="p-3">Tax (18%)</th><th className="p-3">Total</th><th className="p-3">Due</th><th className="p-3">Status</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id} className="border-t border-navy-100" data-testid={`invoice-row-${i.id}`}>
                  <td className="p-3 font-mono text-navy-700">{i.invoice_no}</td>
                  <td className="p-3 text-navy-700">{i.agency_name}</td>
                  <td className="p-3 text-center text-navy-600">{inr(i.amount)}</td>
                  <td className="p-3 text-center text-navy-600">{inr(i.tax)}</td>
                  <td className="p-3 text-center font-bold text-navy-900">{inr(i.total)}</td>
                  <td className="p-3 text-center text-navy-600">{i.due_date}</td>
                  <td className="p-3 text-center"><StatusPill status={i.status} /></td>
                  <td className="p-3 text-right">
                    {i.status === "PAID" ? (
                      <Button size="sm" variant="ghost" onClick={() => window.print()}><Download size={14} /></Button>
                    ) : (
                      <Button size="sm" className="bg-brand hover:bg-brand-600" disabled={paying === i.id} onClick={() => pay(i)} data-testid={`pay-${i.id}`}>{paying === i.id ? "Processing…" : "Pay Now"}</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
